import type { Camera } from '../engine/Camera';
import type { DustField } from '../effects/Dust';

/**
 * Renders the ambient dust field.
 *
 * Draws each visible mote as a tiny soft dot whose alpha breathes over time
 * (per-mote phase). Frustum-culled, single save/restore, no shadowBlur per
 * mote — very cheap.
 */
export class DustRenderer {
  draw(ctx: CanvasRenderingContext2D, camera: Camera, field: DustField, time: number): void {
    const bounds = camera.getVisibleBounds();
    const z = camera.zoom;
    const cx = camera.viewportW * 0.5 + camera.shakeX;
    const cy = camera.viewportH * 0.5 + camera.shakeY;
    const cax = camera.position.x;
    const cay = camera.position.y;

    ctx.save();
    // One soft glow setting for the whole batch.
    ctx.shadowBlur = 4;

    const motes = field.motes;
    for (let i = 0; i < motes.length; i++) {
      const m = motes[i]!;
      if (m.x < bounds.minX || m.x > bounds.maxX) continue;
      if (m.y < bounds.minY || m.y > bounds.maxY) continue;
      const breath = 0.5 + 0.5 * Math.sin(time * 0.9 + m.phase);
      const alpha = 0.18 + 0.22 * breath;
      const r = Math.max(0.6, m.r * z * (0.85 + 0.3 * breath));
      const sx = (m.x - cax) * z + cx;
      const sy = (m.y - cay) * z + cy;
      ctx.globalAlpha = alpha;
      ctx.shadowColor = `hsl(${m.hue} 80% 75%)`;
      ctx.fillStyle = `hsl(${m.hue} 80% 78%)`;
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
    ctx.globalAlpha = 1;
  }
}
